import _each from 'lodash/each';
import _map from 'lodash/map';
import asyncRetry from 'async/retry';
import util from './util';
import Storage from './storage';
import Platform from './platform';

export default {
  init,
  isReady,
  getCustomBaseUrl,
  setCustomBaseUrl,
  getBaseUrl,
  get,
  post,
  put,
  del,
  cdnGet,
  request,
  rawRequest,
};

const DEFAULT_TIMEOUT = 10 * 1000;
const CUSTOM_BASE_URL_KEY = 'HARBOUR_SWAP_CUSTOM_BASE_URL';

const DEV_BASE_URL = 'https://api-cdn-prod.harbourswap.com';
const DEV_BASE_CDN_URL = 'https://api-cdn-prod.harbourswap.com';
const PROD_BASE_URL = 'https://api-cdn-prod.harbourswap.com';
const PROD_BASE_CDN_URL = 'https://api-cdn-prod.harbourswap.com';

let g_base_url = '';
let g_base_cdn_url = '';
let g_custom_base_url = '';
let g_user_agent = '';

function init(done): void {
  const env = util.getEnvironment();
  if (env === 'dev') {
    g_base_url = DEV_BASE_URL;
    g_base_cdn_url = DEV_BASE_CDN_URL;
    util.log('Api.init: set dev urls:', g_base_url);
  } else {
    g_base_url = PROD_BASE_URL;
    g_base_cdn_url = PROD_BASE_CDN_URL;
  }

  if (Platform && Platform.RNInfoManifestBridge) {
    const { RNInfoManifestBridge } = Platform;
    const ver = RNInfoManifestBridge.shortVersion;
    const os = Platform.OS === 'ios' ? 'ios' : 'android';
    const os_ver =
      Platform.OS === 'ios'
        ? RNInfoManifestBridge.iosVersion
        : RNInfoManifestBridge.androidVersion;
    const device_type = _clean(
      Platform.OS === 'ios'
        ? RNInfoManifestBridge.iosSystemInfoMachine
        : RNInfoManifestBridge.androidDeviceName
    );
    const device_family = _clean(
      Platform.OS === 'ios' ? RNInfoManifestBridge.iosDeviceFamily : 'android'
    ).toLowerCase();
    const language = RNInfoManifestBridge.language;
    g_user_agent = `hexchange/${ver} os/${os} os_ver/${os_ver} device_type/${device_type} device_family/${device_family} language/${language}`;
  }

  Storage.getItem(CUSTOM_BASE_URL_KEY, (err, value) => {
    if (!err && value) {
      util.log('Api.init: custom base url:', value);
      g_custom_base_url = value;
    }
    done();
  });
}

function isReady(): boolean {
  return true;
}
function getCustomBaseUrl(): string {
  return g_custom_base_url;
}
function setCustomBaseUrl(url): void {
  if (!url) {
    g_custom_base_url = null;
  } else {
    url = url.replace(/\/*$/, '');
    g_custom_base_url = url;
  }
  Storage.setItem(CUSTOM_BASE_URL_KEY, g_custom_base_url);
}
if (window) {
  window.getCustomBaseUrl = getCustomBaseUrl;
  window.setCustomBaseUrl = setCustomBaseUrl;
}

function getBaseUrl(): string {
  return g_custom_base_url ? g_custom_base_url : g_base_url;
}
function getCdnUrl(): string {
  return g_custom_base_url ? g_custom_base_url : g_base_cdn_url;
}

function get(opts, done): void {
  opts.method = 'GET';
  request(opts, done);
}
function post(opts, done): void {
  opts.method = 'POST';
  request(opts, done);
}
function put(opts, done): void {
  opts.method = 'PUT';
  request(opts, done);
}
function del(opts, done): void {
  opts.method = 'DELETE';
  request(opts, done);
}
function request(opts, done): void {
  if (opts.url.indexOf('http') !== 0) {
    opts.url = getBaseUrl() + opts.url;
  }

  if (opts.retry) {
    _requestRetry(opts, done);
  } else {
    rawRequest(opts, done);
  }
}
function cdnGet(opts, done): void {
  opts.url = getCdnUrl() + opts.url;
  rawRequest(opts, done);
}

function _requestRetry(params, done): void {
  const opts = {
    times: 5,
    interval: (count) => {
      const interval = 50 * Math.pow(3, Math.min(count, 10));
      return interval;
    },
    errorFilter: (err) => {
      let ret = true;
      if (typeof err === 'number' && err > 0 && err < 500) {
        ret = false;
      }
      return ret;
    },
  };
  asyncRetry(opts, (done) => rawRequest(params, done), done);
}

function rawRequest(args, done): void {
  function request_done(...done_extra): void {
    done(...done_extra);
    done = () => {};
  }

  const { method, track_progress } = args;

  let default_headers = {
    accept: 'application/json',
  };
  if (g_user_agent) {
    default_headers['user-agent'] = g_user_agent;
  }
  let body = null;
  if (args.body instanceof FormData) {
    body = args.body;
    // eslint-disable-next-line no-undef
  } else if (args.body instanceof Blob) {
    body = args.body;
  } else if (args.body) {
    body = JSON.stringify(args.body);
    default_headers['content-type'] = 'application/json';
  }

  let url = args.url;
  if (args.query) {
    const query_s = _map(args.query, (v, k) => {
      return encodeURIComponent(k) + '=' + encodeURIComponent(v);
    }).join('&');
    if (query_s.length > 0) {
      if (url.indexOf('?') > 0) {
        url += '&' + query_s;
      } else {
        url += '?' + query_s;
      }
    }
  }

  const headers = Object.assign({}, default_headers, args.headers);

  const xhr = new XMLHttpRequest();
  if (args.timeout) {
    xhr.timeout = args.timeout;
  } else {
    xhr.timeout = DEFAULT_TIMEOUT;
  }

  xhr.onload = () => {
    let status = xhr.status === 1223 ? 204 : xhr.status;
    let response_body = false;
    let json = false;
    let err = null;

    response_body = xhr.response || xhr.responseText;
    const content_type = xhr.getResponseHeader('content-type');
    const is_json = content_type && content_type.indexOf('json') !== -1;

    if (response_body && response_body.length && is_json) {
      try {
        json = JSON.parse(response_body);
      } catch (e) {
        err = e;
      }
    }

    const header_text = xhr.getAllResponseHeaders();
    const header_list = header_text.split('\r\n');
    const response_headers = {};
    header_list
      .map((h) => h.split(': '))
      .forEach((h) => {
        response_headers[h[0].toLowerCase()] = h[1];
      });

    if (status < 100 || status > 599) {
      err = 'bad_status_code';
    } else if (status > 399) {
      err = status;
    }
    request_done(err, json || response_body, response_headers);
  };

  xhr.onerror = () => {
    request_done('request_failure');
  };
  xhr.ontimeout = () => {
    request_done('timeout');
  };

  xhr.open(method || 'GET', url, true);

  _each(headers, (values, name) => {
    if (!Array.isArray(values)) {
      values = [values];
    }
    values.forEach((value) => {
      xhr.setRequestHeader(name, value);
    });
  });

  if (track_progress) {
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        track_progress((e.loaded / e.total) * 100);
      }
    };
  }

  xhr.send(body);
}
function _clean(s) {
  let ret = '';
  if (s) {
    ret = s.replace(/\s+/g, '_');
  }
  return ret;
}
